import React, { useContext } from 'react'
import BigNumber from 'bignumber.js'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { AutoRow } from 'components/Layout/Row'
import { ModalActions } from 'components/Modal'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { useCurrencyBalance } from 'state/wallet/hooks'
import { Price, Token } from 'vvs-sdk'
import { Text, Box, Button } from 'vvs-uikit'
import styled from 'styled-components'
import xVvsContext from 'views/xVvs/context'
import tokens from 'config/constants/tokens'
import { tryParseAmount } from 'state/swap/hooks'
import AprRow from '../BarCard/AprRow'

interface Props {
  rate: Price
  token: Token
  onDismiss: () => void
  onCalculatorClick: () => void
  isUnstaking: boolean
  apr: number
}

const StyledBox = styled(Box)`
  ${({ theme }) => theme.mediaQueries.md} {
    min-width: 520px;
  }
`

const InputStep = ({ rate, onDismiss, onCalculatorClick, token, isUnstaking, apr }: Props) => {
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()
  const balance = useCurrencyBalance(account, token)

  const { error, state, onChange, goNext } = useContext(xVvsContext)
  const { value } = state
  const isValid = !error

  const insufficientAmount = balance && new BigNumber(value || 0).gt(balance.toExact())
  const disableTransaction = value === '' || new BigNumber(value).eq(0) || insufficientAmount

  const converted = isUnstaking
    ? tryParseAmount(new BigNumber(value ?? 0).toString(), tokens.xvvs)
        ?.multiply(rate ?? '1')
        .toSignificant(18)
    : tryParseAmount(new BigNumber(value ?? 0).toString(), tokens.xvvs)
        ?.divide(rate ?? '1')
        .toSignificant(18)

  const vvsPriceUsd = usePriceVvsUsdc()
  let valueInUsdHint: BigNumber
  if (!isUnstaking) {
    valueInUsdHint = vvsPriceUsd.times(value || 0)
  }

  return (
    <StyledBox>
      <CurrencyInputPanel
        label={isUnstaking ? t('Unstake Amount') : t('Stake Amount')}
        value={value}
        showMaxButton={false}
        currency={token}
        onUserInput={onChange}
        disableCurrencySelect
        id="stake-modal-input"
        showPercentageButtons
        valueInUsdHint={valueInUsdHint}
      />
      <Box mt={3}>
        <AprRow apr={apr} text={t('APR')} small onCalculatorClick={onCalculatorClick} />
      </Box>
      <AutoRow justify="space-between" mt={3}>
        <Text color="textSubtle" small>
          {t('Rates')}
        </Text>
        <Text small>1 xVVS = {rate?.toSignificant(18)} VVS</Text>
      </AutoRow>
      <AutoRow justify="space-between" mt={1}>
        <Text color="textSubtle" small>
          {t('You will receive')}
        </Text>
        <Text small>
          {converted} {isUnstaking ? 'VVS' : 'xVVS'}
        </Text>
      </AutoRow>
      <ModalActions>
        <Button variant="secondary" onClick={onDismiss} width="100%">
          {t('Cancel')}
        </Button>
        <Button width="100%" disabled={!isValid || disableTransaction} onClick={goNext}>
          {!isValid ? error : insufficientAmount ? t('Insufficient balance') : t('Confirm')}
        </Button>
      </ModalActions>
    </StyledBox>
  )
}

export default InputStep
